import { UserApi } from '@/apis';
import RootStore from '.';
import BaseStore from './BaseStore';
import { User } from '@/types/user';
import { flow, makeObservable, observable } from 'mobx';
import { GetUsersRequest, GetUsersResponse } from '@/types/http-payload/user';
import { ResponseData } from '@/types/http';
import { TableState } from '@tanstack/react-table';
import { ROLE } from '@/types/enums';

export default class UserStore extends BaseStore {
    users: User[] = [];
    // landmark フォームの「スポンサー事業者」選択用。paginated `users` を汚さない別 observable。
    businessUsers: { id: number, username: string }[] = [];
    totalPage: number = 0;
    api: UserApi;

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            users: observable,
            businessUsers: observable,
            getUsers: flow.bound,
            loadBusinessUsers: flow.bound,
            changeStatusUser: flow.bound,
            changeRoleUser: flow.bound,
            changeTesterUser: flow.bound,
            // Pattern A (2026-05-15): BUSINESS 直接作成
            createBusinessUser: flow.bound
        });
        this.api = new UserApi();
    }

    *getUsers(searchParams: any, paging?: TableState) {
        try {
            const payload = { ...this.convertPagingFromTableToRequest(paging ?? this.paging), ...searchParams };
            const res: ResponseData<GetUsersResponse> = yield this.rootStore.apiStore.call(this.api, this.api.getUsers, payload);
            if (res.ok) {
                this.users = res.data.elements;
                this.paging = {
                    ...this.paging,
                    sorting: paging?.sorting ?? [],
                    pagination: this.convertPaginationFromRequestToTable(res.data.paginate)
                };
                this.totalRecord = res.data.paginate.totalRecord;
                this.totalPage = res.data.paginate.totalPage;
            }
            return res;
        } catch (error) {
        }
    }

    // 事業者 (BUSINESS) 一覧を landmark フォームの sponsor 選択 dropdown 用に取得。
    // paginated `users` とは別 observable に格納し、ユーザー管理画面の表示を汚さない。
    *loadBusinessUsers() {
        try {
            const res: ResponseData<GetUsersResponse> = yield this.rootStore.apiStore.call(
                this.api,
                this.api.getUsers,
                { roleId: ROLE.BUSINESS, page: 1, size: 1000 }
            );
            if (res.ok) {
                this.businessUsers = (res.data.elements ?? []).map((u: any) => ({
                    id: u.id,
                    username: u.username
                }));
            }
        } catch (error) {
        }
    }

    *changeStatusUser(payload) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.changeStatusUser, payload);
            if (res.ok) {
                return true;
            }
        } catch (error) {
        }
    }

    // U2 (2026-05-06): role 変更 (BUSINESS 付与 / 剥奪)。
    // payload = { id, payload: { role_id: 0 | 1 | 2 } }
    *changeRoleUser(payload: { id: string | number, payload: { role_id: number } }) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.changeRoleUser, payload);
            return res;
        } catch (error) {
            return { ok: false, message: 'role 変更に失敗しました' };
        }
    }

    // テスターの印の付け外し (2026-11-04 §50)。テスター限定のスポットが地図に出て、GPS で引ける。
    *changeTesterUser(payload: { id: string | number, payload: { is_tester: boolean, note?: string | null } }) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.changeTesterUser, payload);
            return res;
        } catch (error) {
            return { ok: false, message: 'テスターの変更に失敗しました' };
        }
    }

    // Pattern A (2026-05-15): 直接 BUSINESS user 作成。
    *createBusinessUser(payload: { username: string, email: string, password: string }) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.createBusinessUser, payload);
            return res;
        } catch (e: any) {
            return e?.data || { ok: false, message: '事業者作成に失敗しました' };
        }
    }

    clean(): void {
        super.clean();
        this.users = [];
    }
}
